// TestLeasePage — /test-lease route: a clearly-marked testing tool (§5).
//
// Two modes, chosen by document type:
//   • Lease (full builder) — pick a property + unit and signer emails (dummy
//     tenant names are prepopulated). "Open test builder" creates a persisted
//     test draft (synthetic group, real property/unit data) and drops you into
//     the real lease builder, where you configure → Generate → Send exactly
//     like a production lease. Signing requests go only to the emails entered.
//   • Adoption addendum (quick send) — the original one-shot test send for an
//     addendum (no builder), preserved as a separate option.
//
// Marked throughout as a testing tool so it's never mistaken for the
// production send flow. No real applicant is contacted.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchSelect } from '@/components/SearchSelect';
import { toast } from '@/components/Toast';
import { ApiError } from '@/lib/api';
import {
  createTestLease,
  listProperties,
  listUnits,
  sendTestLease,
  type DocumentType,
  type PropertyOption,
  type TestSigner,
  type UnitOption,
  unitOptionLabel,
} from '@/hooks/useLeaseLifecycle';
import { randomRealisticName } from '@/lib/testNames';

interface SignerRow extends TestSigner {
  key: number;
}

let nextKey = 1;
// Dummy tenant names are prepopulated (realistic + variable length); the tester
// only has to supply the signing emails. Names stay editable.
const blankRow = (): SignerRow => ({ key: nextKey++, name: randomRealisticName(), email: '' });

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function TestLeasePage(): React.JSX.Element {
  const navigate = useNavigate();

  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [propsLoading, setPropsLoading] = useState(true);
  const [propsError, setPropsError] = useState<string | null>(null);

  const [documentType, setDocumentType] = useState<DocumentType>('lease');
  const [propertyId, setPropertyId] = useState('');

  // Lease-mode unit picker — units load when a property is chosen.
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState<string | null>(null);
  const [unitId, setUnitId] = useState('');

  // Default ON for the addendum quick send: emails the test signers AND the WHP
  // countersigners so the whole order-group flow runs end to end. Still gated
  // server-side by DOCUSEAL_SEND_ENABLED. (Lease mode sends from the builder.)
  const [sendEmail, setSendEmail] = useState(true);
  const [signers, setSigners] = useState<SignerRow[]>(() => [blankRow(), blankRow()]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listProperties();
        if (!cancelled) setProperties(list);
      } catch (err) {
        if (!cancelled) setPropsError(err instanceof Error ? err.message : 'Failed to load properties');
      } finally {
        if (!cancelled) setPropsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load units whenever the property changes (lease mode needs a unit).
  useEffect(() => {
    if (!propertyId) {
      setUnits([]);
      setUnitId('');
      setUnitsError(null);
      return;
    }
    let cancelled = false;
    setUnitsLoading(true);
    setUnitsError(null);
    listUnits(propertyId)
      .then((list) => {
        if (cancelled) return;
        setUnits(list);
        setUnitId((prev) => (list.some((u) => u.unitId === prev) ? prev : (list[0]?.unitId ?? '')));
      })
      .catch((err) => {
        if (!cancelled) setUnitsError(err instanceof Error ? err.message : 'Failed to load units');
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });
    return () => { cancelled = true; };
  }, [propertyId]);

  const validSigners = signers.filter((s) => s.name.trim() && EMAIL_RE.test(s.email.trim()));
  const hasInvalidEmail = signers.some((s) => s.email.trim() && !EMAIL_RE.test(s.email.trim()));

  const isLease = documentType === 'lease';
  const canLaunch =
    !busy && isLease && !!propertyId && !!unitId && validSigners.length > 0 && !hasInvalidEmail;
  const canSendAddendum =
    !busy && !isLease && !!propertyId && validSigners.length > 0 && !hasInvalidEmail;

  const updateSigner = (key: number, patch: Partial<TestSigner>) => {
    setSigners((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  };

  // Lease mode: create the test draft + open the real builder.
  const handleLaunch = async () => {
    if (!canLaunch) return;
    setBusy(true);
    try {
      const { groupId } = await createTestLease({
        propertyId,
        unitId,
        signers: validSigners.map((s) => ({ name: s.name.trim(), email: s.email.trim() })),
      });
      navigate(`/applications/${encodeURIComponent(groupId)}/lease`);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? `Could not start test (HTTP ${err.status})`)
          : err instanceof Error
            ? err.message
            : 'Could not start test lease';
      toast.error(msg, { title: 'Test lease' });
      setBusy(false);
    }
  };

  // Addendum mode: original one-shot quick send.
  const handleSendAddendum = async () => {
    if (!canSendAddendum) return;
    setBusy(true);
    try {
      const result = await sendTestLease({
        propertyId,
        sendEmail,
        documentType: 'adoption_addendum',
        testSigners: validSigners.map((s) => ({ name: s.name.trim(), email: s.email.trim() })),
      });
      toast.success(
        result.status
          ? `Test addendum sent (#${result.docusealSubmissionId}). Status: ${result.status}.`
          : 'Test addendum sent.',
        { title: 'Test addendum' },
      );
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? `Test send failed (HTTP ${err.status})`)
          : err instanceof Error
            ? err.message
            : 'Test send failed';
      toast.error(msg, { title: 'Test send failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 'var(--space-7)' }}>
        <div className="cluster" style={{ gap: 'var(--space-3)' }}>
          <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
            Test lease
          </h1>
          <span className="pill" data-tone="warning">
            Testing tool
          </span>
        </div>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
          Run a full test through the real lease builder, or fire a quick adoption-addendum test. Dummy
          tenant names are prefilled — you just set the property/unit and the signing emails. No real
          applicant is contacted; signing requests go only to the addresses you enter.
        </p>
      </div>

      <div className="alert" data-tone="warning" role="note" style={{ marginBottom: 'var(--space-7)' }}>
        <div className="alert-body">
          This tool is for internal testing only. Do not use real applicant emails.
        </div>
      </div>

      {propsError && (
        <div className="alert" data-tone="danger" role="alert" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">{propsError}</div>
        </div>
      )}

      <div className="stack">
        {/* Document type */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">What do you want to test?</h3>
          </div>
          <div className="field">
            <select
              id="test-doctype"
              className="input"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value as DocumentType)}
              aria-label="Document type"
            >
              <option value="lease">Lease — full builder</option>
              <option value="adoption_addendum">Adoption addendum — quick send</option>
            </select>
          </div>
        </section>

        {/* Property + (lease) unit picker */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Property{isLease ? ' & unit' : ''}</h3>
          </div>
          <div className="stack-sm">
            <div className="field">
              <label htmlFor="prop-select">Property</label>
              <SearchSelect
                id="prop-select"
                options={properties.map((p) => ({
                  value: p.propertyId,
                  label: p.name,
                  sublabel: p.address,
                }))}
                value={propertyId}
                onChange={setPropertyId}
                disabled={propsLoading}
                placeholder={propsLoading ? 'Loading…' : 'Select or search by name or address…'}
                emptyText="No matching properties"
              />
            </div>

            {isLease && (
              <div className="field">
                <label htmlFor="unit-select">Unit</label>
                <select
                  id="unit-select"
                  className="input"
                  value={unitId}
                  onChange={(e) => setUnitId(e.target.value)}
                  disabled={!propertyId || unitsLoading}
                >
                  <option value="">
                    {!propertyId
                      ? 'Select a property first…'
                      : unitsLoading
                        ? 'Loading units…'
                        : units.length === 0
                          ? 'No units found for this property'
                          : 'Select a unit…'}
                  </option>
                  {units.map((u) => (
                    <option key={u.unitId} value={u.unitId}>
                      {unitOptionLabel(u)}
                      {u.address ? ` — ${u.address}` : ''}
                    </option>
                  ))}
                </select>
                {unitsError && (
                  <span className="helper" data-state="error">{unitsError}</span>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Signers */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Test {isLease ? 'tenants' : 'signers'}</h3>
          </div>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: '0 0 var(--space-3)' }}>
            Names are prefilled with dummy values (editable). Enter a real email per signer — these
            receive the signing requests. The first row is the lead applicant.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {signers.map((s, idx) => {
              const emailInvalid = !!s.email.trim() && !EMAIL_RE.test(s.email.trim());
              return (
                <div key={s.key} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
                  <input
                    type="text"
                    className="input"
                    style={{ flex: 1 }}
                    placeholder={`${isLease ? 'Tenant' : 'Signer'} ${idx + 1} name`}
                    value={s.name}
                    onChange={(e) => updateSigner(s.key, { name: e.target.value })}
                    aria-label={`${isLease ? 'Tenant' : 'Signer'} ${idx + 1} name`}
                  />
                  <div style={{ flex: 1.4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <input
                      type="email"
                      className="input"
                      placeholder="test@example.com"
                      value={s.email}
                      onChange={(e) => updateSigner(s.key, { email: e.target.value })}
                      aria-label={`${isLease ? 'Tenant' : 'Signer'} ${idx + 1} email`}
                      aria-invalid={emailInvalid}
                    />
                    {emailInvalid && (
                      <span className="helper" data-state="error">Enter a valid email.</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn"
                    data-variant="ghost"
                    data-size="sm"
                    data-icon-only
                    aria-label={`Remove signer ${idx + 1}`}
                    disabled={signers.length <= 1}
                    onClick={() => setSigners((prev) => prev.filter((r) => r.key !== s.key))}
                  >
                    ×
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              onClick={() => setSigners((prev) => [...prev, blankRow()])}
              style={{ alignSelf: 'flex-start' }}
            >
              + Add {isLease ? 'tenant' : 'signer'}
            </button>
          </div>
        </section>

        {/* Action */}
        <section className="card">
          <div className="stack-sm">
            {isLease ? (
              <>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
                  Opens the full lease builder, prefilled from this unit's AppFolio data. Configure,
                  Generate, then Send from the builder — signing goes to the emails above.
                </p>
                <button
                  type="button"
                  className="btn"
                  onClick={() => void handleLaunch()}
                  disabled={!canLaunch}
                  style={{ alignSelf: 'flex-start', marginTop: 'var(--space-2)' }}
                >
                  {busy ? 'Starting…' : 'Open test builder →'}
                </button>
              </>
            ) : (
              <>
                <label className="choice">
                  <input
                    type="checkbox"
                    className="check"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                  />
                  Send signing emails (full end-to-end test) — emails the test signers AND the WHP
                  countersigner (Landlord). Gated by DOCUSEAL_SEND_ENABLED.
                </label>
                <button
                  type="button"
                  className="btn"
                  onClick={() => void handleSendAddendum()}
                  disabled={!canSendAddendum}
                  style={{ alignSelf: 'flex-start', marginTop: 'var(--space-3)' }}
                >
                  {busy ? 'Sending test…' : 'Send test addendum'}
                </button>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
